import { ClientLinkPrimary } from '@fukku/button/ClientLink/Primary/ClientLinkPrimary'
import { ButtonSecondary } from '@fukku/button/Secondary/ButtonSecondary'
import textActionM from '@fukku/styles/texts/textActionM.module.css'
import textBodyM from '@fukku/styles/texts/textBodyM.module.css'
import { SHOPPING_CART_LOCATION } from 'cart/constants/constants'
import { useBagTotalItems } from 'cart/hooks/queries/useBagTotalItems'
import { useCartProduct } from 'cart/hooks/useCartProduct'
import type { ItemDto } from 'cart/services/graphql.types'
import { cx } from 'classix'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { replacePlaceholders } from 'labels/lib/replacePlaceholders'
import { useCartUrl } from 'links/cart/hooks/useCartUrl'
import { useCheckoutLinks } from 'links/checkout/useCheckoutLinks'
import Image from 'next/image'
import { useState } from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import { Price } from '../../../shoppingCart/components/Price/Price'
import { useAnalytics } from '../../hooks/useAnalytics'
import { getProductImageTest } from '../../utils/productMappers'
import styles from './AddedToCartNotificationContent.module.css'

export const AddedToCartNotificationContent = ({
	item,
	cartItems,
}: {
	readonly item: ItemDto
	readonly cartItems: ItemDto[]
}) => {
	const { cartProduct, productInfo } = useCartProduct(item)
	const { totalItems } = useBagTotalItems()
	const { t } = useLabels()
	const { navigateToCheckout } = useCheckoutLinks()
	const { sendClickViewCart } = useAnalytics()
	const [loaded, setLoaded] = useState(false)
	const { isLargeOrGreater } = useResponsive()
	const { sendBeginCheckoutEECEvent } = useAnalytics()
	const cartUrl = useCartUrl()

	const goToCartId = 'bag.preview.goToFullpage'
	const goToCartDataTestId = 'go-to-cart-button'
	const goToCheckoutId = 'bag.preview.checkout.button'

	if (!cartProduct) {
		return <div className={styles.skeleton} />
	}

	const productImage = getProductImageTest(
		productInfo ?? undefined,
		cartProduct
	)

	return (
		<div className={styles.container}>
			<div className={styles.body}>
				{productImage.img && (
					<div className={styles.imageWrapper}>
						<Image
							alt={productImage.alt}
							data-testid='bag.preview.item.image'
							src={productImage.img}
							width={120}
							height={168}
							onLoad={() => setLoaded(true)}
							className={cx(styles.image, loaded && styles.imageAnimated)}
						/>
					</div>
				)}
				<div className={styles.details}>
					<p className={styles.productName}>{cartProduct?.info?.name}</p>
					<div className={styles.price}>
						<Price
							price={cartProduct.price}
							promoCode={undefined}
							textStyle={textBodyM.className}
							horizontal
						/>
					</div>
					<div className={styles.detailsWrapper}>
						<p className={styles.size}>{cartProduct?.info?.sizeName}</p>
						<p className={styles.color}>{cartProduct?.info?.colorName}</p>
					</div>
				</div>
			</div>
			<div className={styles.buttonWrapper}>
				{isLargeOrGreater ? (
					<ButtonSecondary
						id={goToCheckoutId}
						data-testid={goToCheckoutId}
						fullWidth
						className={textActionM.className}
						onClick={() => {
							void sendBeginCheckoutEECEvent(
								SHOPPING_CART_LOCATION.PREVIEW,
								cartItems
							)
							sendClickViewCart()
							navigateToCheckout()
						}}
					>
						{t('addToBag.checkout.button')}
					</ButtonSecondary>
				) : null}
				<ClientLinkPrimary
					data-testid={goToCartDataTestId}
					id={goToCartId}
					fullWidth
					className={textActionM.className}
					href={cartUrl.href}
					onClick={() => sendClickViewCart()}
				>
					{replacePlaceholders(t('addToBag.seeBag.button'), {
						1: totalItems ?? 0,
					})}
				</ClientLinkPrimary>
			</div>
		</div>
	)
}
